import { AssistantMessageToggleSourcesButton } from "#features/conversations/components/conversation-source/AssistantMessageToggleSourcesButton.tsx";
import { AssistantMessageBlocked } from "#features/conversations/components/conversation-view/AssistantMessageBlocked.tsx";
import { AssistantMessageCancelled } from "#features/conversations/components/conversation-view/AssistantMessageCancelled.tsx";
import { AssistantMessageConnection } from "#features/conversations/components/conversation-view/AssistantMessageConnection.tsx";
import { AssistantMessageContent } from "#features/conversations/components/conversation-view/AssistantMessageContent.tsx";
import { AssistantMessageError } from "#features/conversations/components/conversation-view/AssistantMessageError.tsx";
import { AssistantMessageFileUploadConfirmation } from "#features/conversations/components/conversation-view/AssistantMessageFileUploadConfirmation.tsx";
import { AssistantMessageMemoryUpdated } from "#features/conversations/components/conversation-view/AssistantMessageMemoryUpdated.tsx";
import { AssistantMessagePageActionButtons } from "#features/conversations/components/conversation-view/AssistantMessagePageActionButtons.tsx";
import { AssistantMessagePersonProfile } from "#features/conversations/components/conversation-view/AssistantMessagePersonProfile.tsx";
import { AssistantMessageThinkingSteps } from "#features/conversations/components/conversation-view/AssistantMessageThinkingSteps.tsx";
import { AssistantMessageToolbar } from "#features/conversations/components/conversation-view/AssistantMessageToolbar.tsx";
import { AssistantMessageUnauthenticated } from "#features/conversations/components/conversation-view/AssistantMessageUnauthenticated.tsx";
import { MessageToolApprovals } from "#features/conversations/components/conversation-view/MessageToolApprovals.tsx";
import { AssistantMessageGeneratedFiles } from "#features/conversations/components/generated-files/AssistantMessageGeneratedFiles.tsx";
import { useMessageCitations } from "#features/conversations/hooks/useMessageCitations.ts";
import type { ConversationMessageModel } from "#features/conversations/types/ConversationMessageModel.ts";
import type { CitedConversation } from "#features/conversations/hooks/useMessageContent.ts";
import { Flex } from "@hopper-ui/components";
import { memo, useMemo, type ReactNode, type RefObject } from "react";

interface Props {
    ref: RefObject<HTMLDivElement | null> | null;
    message: ConversationMessageModel;
    cited: CitedConversation;
    unsettledApprovals: ReadonlySet<string>;
    // The message picks up an answer that paused on an approval card, so it closes the gap to that message.
    isContinuation?: boolean;
    // The answer goes on in the next message, which carries the feedback row.
    isContinued?: boolean;
}

const AssistantMessageView = ({ ref, message, cited, unsettledApprovals, isContinuation = false, isContinued = false }: Props) => {
    const { id, assistantStatus, connection, personProfile, assistantError } = message;

    // The chip and the modal show what the answer cites, numbered as its pills are; `sources` keeps
    // the full collection for the thinking-step count and for cross-turn citation resolution.
    const { citations, numbersByIndex } = useMessageCitations(cited, message);

    const isLatestMessage = useMemo(() => !!ref, [ref]);
    const toolApprovals = <MessageToolApprovals conversationId={cited.conversationId} message={message} unsettled={unsettledApprovals} />;
    // A turn that fails after it paused keeps its approval open, so the prompt stays under the notice.
    const withToolApprovals = (notice: ReactNode) => (
        <Flex direction="column" gap="inline-md">
            {notice}
            {toolApprovals}
        </Flex>
    );

    if (assistantStatus === "blocked") {
        return withToolApprovals(<AssistantMessageBlocked ref={ref} />);
    }

    if (assistantStatus === "error") {
        return withToolApprovals(<AssistantMessageError ref={ref} messageId={id} error={assistantError} />);
    }

    if (assistantStatus === "unauthenticated") {
        return withToolApprovals(<AssistantMessageUnauthenticated ref={ref} />);
    }

    return (
        <Flex ref={ref} direction="column" gap="inline-sm" className={isContinuation ? "wai-assistant-message-continuation" : undefined}>
            <AssistantMessageMemoryUpdated message={message} />
            <Flex
                direction="column"
                gap="inline-md"
                className="wai-assistant-message"
                padding="inset-md"
                borderRadius="pill"
                wordBreak="break-word"
            >
                <Flex direction="column" gap="inline-md" fontSize="body-sm" color="neutral">
                    <AssistantMessageThinkingSteps message={message} />
                    <AssistantMessageContent
                        message={message}
                        cited={cited}
                        citationNumbers={numbersByIndex}
                    />
                    <AssistantMessageCancelled
                        message={message}
                    />
                    <AssistantMessageGeneratedFiles files={message.generatedFiles} />
                </Flex>
                {personProfile &&
                    <AssistantMessagePersonProfile
                        profile={personProfile}
                        message={message}
                        cited={cited}
                        citationNumbers={numbersByIndex}
                    />
                }
                {connection &&
                    <AssistantMessageConnection conversationId={cited.conversationId} connection={connection} isSettled={assistantStatus === "idle"} />
                }
                {toolApprovals}
                {assistantStatus === "idle" &&
                <>
                    {isLatestMessage &&
                    <Flex direction="column" gap="inline-sm">
                        <AssistantMessagePageActionButtons
                            conversationId={cited.conversationId}
                            message={message}
                        />
                        <AssistantMessageFileUploadConfirmation
                            conversationId={cited.conversationId}
                            messageId={message.id}
                            files={message.files}
                        />
                    </Flex>
                    }
                    <Flex wrap="wrap" alignItems="center">
                        <AssistantMessageToggleSourcesButton
                            message={message}
                            citations={citations}
                        />
                        {!message.isUnsaved && !isContinued &&
                            <AssistantMessageToolbar
                                conversationId={cited.conversationId}
                                message={message}
                            />
                        }
                    </Flex>
                </>
                }
            </Flex>
        </Flex>
    );
};

// Read from its conversation only through `cited`, the same object while a streamed chunk adds text, so an earlier answer
// doesn't render, or parse its markdown, again with each chunk of the one streaming below it.
export const AssistantMessage = memo(AssistantMessageView);
